import { z } from 'zod';

export const apiEnvSchema = z.object({
    GEMINI_API_KEY: z.string().min(1),
    SUPABASE_URL: z.string().min(1),
    SUPABASE_ANON_KEY: z.string().min(1),
    ENVIRONMENT: z.string().default('development'),
    SKIP_AUTH: z.string().optional(),
    ENCRYPTION_KEY: z.string().optional(),
    ARCHIVE_RETENTION_DAYS: z.string().optional(),
    AI_ANALYSIS_ENABLED: z.string().optional(),
    AI_MAX_TEXT_CHARS: z.string().optional(),
    AI_MAX_AUDIO_DURATION_SECONDS: z.string().optional(),
    AI_MAX_AUDIO_FILE_SIZE_BYTES: z.string().optional(),
});

export type ApiEnv = z.infer<typeof apiEnvSchema>;

export const DEFAULT_ARCHIVE_RETENTION_DAYS = 30;
export const DEFAULT_AI_USAGE_POLICY = {
    analysisEnabled: true,
    maxTextChars: 20000,
    maxAudioDurationSeconds: 900,
    maxAudioFileSizeBytes: 26214400,
} as const;

export interface AiUsagePolicy {
    analysisEnabled: boolean;
    maxTextChars: number;
    maxAudioDurationSeconds: number;
    maxAudioFileSizeBytes: number;
}

export function parseApiEnv(env: unknown): ApiEnv {
    return apiEnvSchema.parse(env);
}

export function getArchiveRetentionDays(env: Pick<Partial<ApiEnv>, 'ARCHIVE_RETENTION_DAYS'>): number {
    const rawValue = env.ARCHIVE_RETENTION_DAYS;

    if (!rawValue) {
        return DEFAULT_ARCHIVE_RETENTION_DAYS;
    }

    const retentionDays = Number(rawValue);

    if (!Number.isInteger(retentionDays) || retentionDays < 1) {
        return DEFAULT_ARCHIVE_RETENTION_DAYS;
    }

    return retentionDays;
}

function getPositiveInteger(value: string | undefined, fallback: number): number {
    if (!value) {
        return fallback;
    }

    const parsed = Number(value);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export function getAiUsagePolicy(env: Partial<ApiEnv>): AiUsagePolicy {
    return {
        analysisEnabled: env.AI_ANALYSIS_ENABLED === undefined
            ? DEFAULT_AI_USAGE_POLICY.analysisEnabled
            : env.AI_ANALYSIS_ENABLED !== 'false',
        maxTextChars: getPositiveInteger(env.AI_MAX_TEXT_CHARS, DEFAULT_AI_USAGE_POLICY.maxTextChars),
        maxAudioDurationSeconds: getPositiveInteger(
            env.AI_MAX_AUDIO_DURATION_SECONDS,
            DEFAULT_AI_USAGE_POLICY.maxAudioDurationSeconds,
        ),
        maxAudioFileSizeBytes: getPositiveInteger(
            env.AI_MAX_AUDIO_FILE_SIZE_BYTES,
            DEFAULT_AI_USAGE_POLICY.maxAudioFileSizeBytes,
        ),
    };
}
